/**
 * usage.queries.ts (Shared)

 * Shared TanStack Query hooks for fetching usage data (daily, monthly, yearly)
 * used by both Dashboard and Customer modules.
 *
 * Each hook  calls the shared `usage.service.ts` to handle API requests
 *
 * - If "id" is passed  then fetches usage for a specific customer(Customer Detail Page).
 * - If "id" is omitted  then fetches total usage for all customers (Home Screen).
 * - Optional `tenantId` is passed through to the service for `?tenantId=` on usage APIs.
 
 */

import { useQuery } from '@tanstack/react-query';
import { useCustomerService } from '../services/usage.service';
import { getMsUntilNextQuarterHour } from '@/shared/utils/time.utils';


const nextQuarterRefreshTime = getMsUntilNextQuarterHour();

export function useUsageMutations() {
  const service = useCustomerService();

  return {
    useGetDailyUsageQuery: (
      date: string | undefined,
      tenantId?: string | null
    ) => {
      return useQuery<{
        points: Array<{ x: string; y: number | null }>;
        totalDayUsage?: number | null;
      }>({
        queryKey: ['customers', 'usage', 'daily', date, tenantId ?? null],
        queryFn: () => {
          if (!date) throw new Error('Missing date for usage query');
          return service.getDailyUsage(date, undefined, tenantId);
        },
        enabled: !!date,
        refetchInterval: nextQuarterRefreshTime,
        refetchIntervalInBackground: true,
        staleTime:nextQuarterRefreshTime,
      });
    },

    useGetCustomerDailyUsageQuery: (
      date: string | undefined,
      id?: string | undefined,
      tenantId?: string | null
    ) => {
      return useQuery<{
        points: Array<{ x: string; y: number | null }>;
        totalDayUsage?: number | null;
      }>({
        queryKey: ['customers', 'usage', 'daily', date, id, tenantId ?? null],
        queryFn: () => {
          if (!date) throw new Error('Missing date for usage query');
          return service.getDailyUsage(date, id, tenantId);
        },
        enabled: !!date && !!id,
        staleTime:  60 * 1000
      });
    },

    useGetCustomerMonthlyUsageQuery: (
      year: number | undefined,
      month: number | undefined,
      id?: string | undefined,
      tenantId?: string | null
    ) => {
      return useQuery<{
        points: Array<{ x: string; y: number | null }>;
        totalMonthlyUsage?: number | null;
      }>({
        queryKey: [
          'customers',
          'usage',
          'monthly',
          year,
          month,
          id,
          tenantId ?? null,
        ],
        queryFn: () => {
          if (typeof year !== 'number' || typeof month !== 'number')
            throw new Error('Missing id/year/month for monthly usage query');
          return service.getMonthlyUsage(year, month, id, tenantId);
        },
        enabled: typeof year === 'number' && typeof month === 'number',
        staleTime: 60 * 1000 
      });
    },
    useGetCustomerYearlyUsageQuery: (
      year: number | undefined,
      id?: string | undefined,
      tenantId?: string | null
    ) => {
      return useQuery<{
        points: Array<{ x: string; y: number | null }>;
        totalYearUsage?: number | null;
      }>({
        queryKey: ['customers', 'usage', 'yearly', year, id, tenantId ?? null],
        queryFn: () => {
          if (typeof year !== 'number')
            throw new Error('Missing id/year for yearly usage query');
          return service.getYearlyUsage(year, id, tenantId);
        },
        enabled: typeof year === 'number',
        staleTime:  60 * 1000
      });
    },
  };
}

